import React, { useState, useEffect } from "react";
import {
  Popover,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Button,
  Box,
  Typography,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DateRangePicker from "../../Sections/Common/DateRangePicker";
import "./DataTable.css";

const FilterDrawer = ({
  open,
  anchorEl,
  onClose,
  filters = [],
  filterValues = {},
  onApply,
  onReset
}) => {
  const [draftValues, setDraftValues] = useState({});

  useEffect(() => {
    if (open) {
      setDraftValues(filterValues || {});
    }
  }, [open, filterValues]);

  const handleChange = (name, value) => {
    setDraftValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApply = () => {
    onApply(draftValues);
    onClose();
  };

  const handleReset = () => {
    const cleared = {};
    filters.forEach((f) => {
      if (f.type === "multiSelect") {
        cleared[f.name] = [];
      } else if (f.type === "dateRange") {
        cleared[f.name] = null;
      } else {
        cleared[f.name] = f.defaultValue !== undefined ? f.defaultValue : "all";
      }
    });
    setDraftValues(cleared);
    onReset(cleared);
    onClose();
  };

  const renderFilterInput = (filter) => {
    const value = draftValues[filter.name];

    switch (filter.type) {
      case "search":
        return (
          <TextField
            fullWidth
            size="small"
            value={value || ""}
            placeholder={filter.placeholder || "Search..."}
            onChange={(e) => handleChange(filter.name, e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
              fieldset: {
                borderColor: "#e1e3ea !important",
              }
            }}
          />
        );

      case "dateRange":
        return (
          <DateRangePicker
            selectedRange={value || null}
            onRangeChange={(range) => handleChange(filter.name, range)}
            placeholder={filter.placeholder || "Select date range"}
          />
        );

      case "multiSelect":
        return (
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={value || []}
              onChange={(e) => handleChange(filter.name, e.target.value)}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return <em style={{ color: "#999999", fontSize: "14px", fontStyle: "normal" }}>{filter.placeholder || "Select Multiple"}</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((val) => {
                      const opt = filter.options?.find((o) => o.value === val);
                      return <Chip key={val} label={opt?.label || val} size="small" />;
                    })}
                  </Box>
                );
              }}
              sx={{
                "& .MuiSelect-select": {
                  padding: "10.5px 14px",
                  textAlign: "start",
                },
                fieldset: {
                  borderColor: "#e1e3ea !important",
                  borderRadius: "8px",
                },
              }}
            >
              {filter.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "select":
      case "status":
      case "portal":
      case "tenant":
      default:
        return (
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={value !== undefined ? value : (filter.defaultValue !== undefined ? filter.defaultValue : "all")}
              onChange={(e) => handleChange(filter.name, e.target.value)}
              input={<OutlinedInput />}
              sx={{
                "& .MuiSelect-select": {
                  padding: "10.5px 14px",
                  textAlign: "start",
                },
                fieldset: {
                  borderColor: "#e1e3ea !important",
                  borderRadius: "8px",
                },
              }}
            >
              {filter.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right"
      }}
      slotProps={{
        paper: {
          style: {
            width: "340px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e1e3ea",
            marginTop: "8px",
            padding: "20px",
            overflow: "visible"
          }
        }
      }}
    >
      <Box className="filter-popover-content">
        <Box className="filter-popover-header">
          <Typography className="filter-popover-title">Filters</Typography>
          <IconButton onClick={onClose} size="small" style={{ color: "#a1a5b7" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className="filter-popover-body">
          {filters.map((filter) => (
            <Box key={filter.name} className="filter-popover-field">
              <Typography className="filter-drawer-label">
                {filter.label}
              </Typography>
              {renderFilterInput(filter)}
            </Box>
          ))}
        </Box>

        <Box className="filter-popover-footer">
          <Button className="filter-btn-reset" onClick={handleReset} size="small">
            Reset
          </Button>
          <Button className="filter-btn-apply" onClick={handleApply} size="small">
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default FilterDrawer;
